import React, { useMemo } from 'react';
import { getNumberMeaning, reduceNumber } from '../domains/numerology';

interface NumerologicalTextReflectionProps {
  text: string;
  compact?: boolean;
}

interface SubsectionAnalysis {
  raw: string;
  total: number;
  reduced: number;
  meaning: ReturnType<typeof getNumberMeaning>;
}

interface LineAnalysis {
  raw: string;
  subsections: SubsectionAnalysis[];
  lineReduced: number;
  lineMeaning: ReturnType<typeof getNumberMeaning>;
}

const PYTHAGOREAN_VALUES: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, I: 9,
  J: 1, K: 2, L: 3, M: 4, N: 5, O: 6, P: 7, Q: 8, R: 9,
  S: 1, T: 2, U: 3, V: 4, W: 5, X: 6, Y: 7, Z: 8
};

const NUMBER_COLORS: Record<number, string> = {
  0: '#f43f5e',
  1: '#f59e0b',
  2: '#38bdf8',
  3: '#f97316',
  4: '#22c55e',
  5: '#eab308',
  6: '#34d399',
  7: '#6366f1',
  8: '#a78bfa',
  9: '#ec4899',
  11: '#fb7185',
  22: '#06b6d4',
  33: '#facc15',
  44: '#93c5fd'
};

function withAlpha(color: string, alphaHex: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) {
    return `${color}${alphaHex}`;
  }

  return color;
}

export function getNumerologyCharacterValue(character: string): number | null {
  if (/\d/.test(character)) {
    return Number(character);
  }

  const upper = character.toUpperCase();
  return PYTHAGOREAN_VALUES[upper] ?? null;
}

function getCharacterColor(character: string): string {
  const value = getNumerologyCharacterValue(character);
  if (value === null) {
    return '#94a3b8';
  }

  const reduced = reduceNumber(value);
  return NUMBER_COLORS[reduced] ?? '#94a3b8';
}

export function getNumerologyCharacterColor(character: string): string {
  return getCharacterColor(character);
}

export function getNumerologyReducedColor(value: number): string {
  return NUMBER_COLORS[reduceNumber(value)] ?? '#94a3b8';
}

export function getNumerologyPaletteFromText(text: string): string[] {
  const uniqueColors: string[] = [];

  text.split('').forEach((char) => {
    const value = getNumerologyCharacterValue(char);
    if (value === null) {
      return;
    }

    const color = getNumerologyReducedColor(value);
    if (!uniqueColors.includes(color)) {
      uniqueColors.push(color);
    }
  });

  return uniqueColors;
}

export function getEquilibriumGradientFromText(text: string, fallbackColor = '#6366f1'): string {
  const palette = getNumerologyPaletteFromText(text);

  if (palette.length === 0) {
    return `linear-gradient(135deg, ${withAlpha(fallbackColor, '22')} 0%, ${withAlpha(fallbackColor, '0f')} 100%)`;
  }

  if (palette.length === 1) {
    const only = palette[0];
    return `linear-gradient(135deg, ${withAlpha(only, '2e')} 0%, ${withAlpha(only, '14')} 100%)`;
  }

  const first = palette[0];
  const middle = palette[Math.floor(palette.length / 2)];
  const last = palette[palette.length - 1];

  return `linear-gradient(135deg, ${withAlpha(first, '33')} 0%, ${withAlpha(middle, '2b')} 50%, ${withAlpha(last, '33')} 100%)`;
}

function analyzeSubsection(raw: string): SubsectionAnalysis {
  const total = raw
    .split('')
    .reduce((sum, char) => {
      const value = getNumerologyCharacterValue(char);
      return sum + (value ?? 0);
    }, 0);

  const reduced = reduceNumber(total);

  return {
    raw,
    total,
    reduced,
    meaning: getNumberMeaning(reduced)
  };
}

const NumerologicalTextReflection: React.FC<NumerologicalTextReflectionProps> = ({ text, compact = false }) => {
  const analysis = useMemo(() => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    return lines.map((line) => {
      const subsectionParts = line
        .split(/\s*[\u2022\u00b7]\s*/)
        .map((part) => part.trim())
        .filter(Boolean);

      const subsections = (subsectionParts.length > 0 ? subsectionParts : [line]).map(analyzeSubsection);

      const lineTotal = subsections.reduce((sum, subsection) => sum + subsection.reduced, 0);
      const lineReduced = reduceNumber(lineTotal);

      return {
        raw: line,
        subsections,
        lineReduced,
        lineMeaning: getNumberMeaning(lineReduced)
      } as LineAnalysis;
    });
  }, [text]);

  if (!text.trim()) {
    return null;
  }

  return (
    <div style={{ marginTop: compact ? '0.75rem' : '1rem' }}>
      {!compact && (
        <div style={{ marginBottom: '0.75rem', color: '#a78bfa', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.04em' }}>
          NUMEROLOGICAL REFLECTION LAYERS
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '0.5rem' : '0.75rem' }}>
        {analysis.map((line, lineIndex) => (
          <div
            key={`${line.raw}-${lineIndex}`}
            style={{
              background: compact ? 'rgba(51, 65, 85, 0.45)' : 'rgba(30, 41, 59, 0.85)',
              border: `1px solid ${NUMBER_COLORS[line.lineReduced] ?? '#64748b'}55`,
              borderRadius: '10px',
              padding: compact ? '0.6rem' : '0.85rem'
            }}
          >
            <div
              style={{
                color: '#e2e8f0',
                fontSize: compact ? '0.8rem' : '0.95rem',
                lineHeight: 1.6,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                marginBottom: '0.5rem'
              }}
            >
              {line.raw.split('').map((char, charIndex) => (
                <span
                  key={`${lineIndex}-${charIndex}`}
                  style={{ color: getCharacterColor(char) }}
                  title={getNumerologyCharacterValue(char) !== null ? `${char.toUpperCase()} -> ${reduceNumber(getNumerologyCharacterValue(char) ?? 0)}` : char}
                >
                  {char}
                </span>
              ))}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginBottom: compact ? 0 : '0.45rem' }}>
              {line.subsections.map((section, sectionIndex) => (
                <span
                  key={`${section.raw}-${sectionIndex}`}
                  style={{
                    fontSize: '0.72rem',
                    color: NUMBER_COLORS[section.reduced] ?? '#cbd5e1',
                    border: `1px solid ${NUMBER_COLORS[section.reduced] ?? '#64748b'}55`,
                    background: 'rgba(15, 23, 42, 0.7)',
                    borderRadius: '999px',
                    padding: '0.2rem 0.55rem'
                  }}
                  title={section.meaning.essence}
                >
                  {section.reduced}: {section.meaning.title}
                </span>
              ))}
            </div>

            {!compact && (
              <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                Story Arc {lineIndex + 1}: {line.lineMeaning.title} ({line.lineReduced}) - {line.lineMeaning.essence}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NumerologicalTextReflection;